// Inspiration: https://github.com/callstack/react-native-paper/blob/master/src/components/IconButton.tsx

import React, { useCallback, useMemo } from "react";
import { StyleProp, ViewStyle, StyleSheet, TextStyle } from "react-native";
import { useTheme } from "../../theme";
import { Icon, IconProps } from "../icon";
import { Tooltip } from "react-native-paper";
import { Pressable } from "react-native";

type IconButtonProps = IconProps & {
    disabled?: boolean;
    onPress: () => void;
    tooltip?: string;
    style?: StyleProp<ViewStyle | TextStyle>;
};

export const IconButton = React.memo(function IconButton({
    name,
    color,
    size,
    variant,
    disabled,
    style,
    onPress,
    tooltip,
    testID,
}: IconButtonProps) {
    const { colors } = useTheme();

    const iconColor = useMemo(
        () =>
            StyleSheet.flatten(style as TextStyle)?.color ||
            (disabled ? colors.grey500 : color),
        [color, colors.grey500, disabled, style]
    );

    const pressableStyle = useMemo(() => [styles.container, style], [style]);

    const resolvePressableStyle = useCallback(
        ({ pressed }) =>
            pressed ? [pressableStyle, styles.pressed] : pressableStyle,
        [pressableStyle]
    );

    const component = (
        <Pressable
            onPress={onPress}
            style={resolvePressableStyle}
            disabled={disabled}
            testID={testID}
        >
            <Icon name={name} size={size} color={iconColor} variant={variant} />
        </Pressable>
    );

    return tooltip ? (
        <Tooltip title={tooltip} leaveTouchDelay={0}>
            {component}
        </Tooltip>
    ) : (
        component
    );
});

const styles = StyleSheet.create({
    container: {
        padding: 6,
    },
    pressed: {
        opacity: 0.7,
    },
});
